import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, Loader2, Clock } from "lucide-react";
import { useConfig } from "../hooks/useConfig";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookButton = ({ className = "", variant = "outline" }) => {
  const { booking_url } = useConfig();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!booking_url) return null;

  const styles =
    variant === "solid"
      ? "bg-white text-black hover:bg-zinc-200"
      : "border border-zinc-700 text-white hover:border-blue-500 hover:text-blue-400";

  const proceed = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/terms/accept`, { context: "calendar_booking" });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Terms acceptance logging failed (booking continues):", err);
    }
    setSaving(false);
    setOpen(false);
    setChecked(false);
    window.open(booking_url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button
        data-testid="book-appointment-button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-sm px-5 py-3 text-sm font-semibold transition-colors ${styles} ${className}`}
      >
        <CalendarCheck className="h-4 w-4" />
        Book Appointment
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setChecked(false); }}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Before you book</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Please review and accept our terms to continue to the booking calendar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/30 rounded-sm p-3" data-testid="booking-notice">
            <Clock className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-zinc-300 text-xs leading-relaxed">
              Online booking requires at least <span className="font-semibold text-amber-400">4 hours advance notice</span>.
              Need service sooner? <a href={PHONE_TEL} className="text-blue-400 font-semibold" data-testid="booking-notice-call">Call or text {PHONE_DISPLAY}</a> — same-day appointments are typically available.
            </p>
          </div>
          <div className="flex items-start gap-3 bg-[#0a0a0a] border border-zinc-800 rounded-sm p-4">
            <Checkbox
              id="booking-terms-checkbox"
              data-testid="booking-terms-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(v === true)}
              className="mt-0.5 border-zinc-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
            />
            <label htmlFor="booking-terms-checkbox" className="text-zinc-400 text-xs leading-relaxed cursor-pointer">
              I have read and agree to Mason's Mobile Mechanic's{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-terms-link"
                className="text-blue-400 underline underline-offset-2"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service and Repair Authorization
              </Link>
              . I authorize Mason's Mobile Mechanic to inspect, diagnose, and perform the requested services on my vehicle.
            </label>
          </div>
          <DialogFooter>
            <button
              data-testid="booking-continue-button"
              onClick={proceed}
              disabled={!checked || saving}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-sm px-6 py-3 text-sm font-semibold transition-colors"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
              Agree &amp; Continue to Booking
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
